<template>
    <div>
      <div v-if="loading" class="has-text-centered" style="padding: 40px;">
        <b-loading :is-full-page="false" v-model="loading"></b-loading>
        <p>Cargando datos de financiamiento...</p>
      </div>
  
      <div v-else-if="processedFinancingData.mensualidades && processedFinancingData.mensualidades.length > 0">
        <div class="box" style="margin-bottom: 1rem; text-align: right;">
          <b-button 
            type="is-primary" 
            icon-left="plus"
            @click="openManualPaymentModal"
            :disabled="!member._id"
          >
            Registrar Pago Manual
          </b-button>
        </div>
  
        <div class="box">
          <p class="title is-5" style="margin-bottom: 1rem;">Mensualidades ({{ processedFinancingData.total }}/{{ processedFinancingData.mensualidades[0].TotalMensualidades }})</p>
          
          <b-table
            :data="processedFinancingData.mensualidades"
            :loading="loading"
            paginated
            per-page="10"
            pagination-simple
            hoverable
            mobile-cards
          >
            <b-table-column field="Mensualidad" label="Mensualidad" v-slot="props">
              <span class="tag is-primary">{{ props.row.Mensualidad }}/{{ props.row.TotalMensualidades }}</span>
            </b-table-column>
  
            <b-table-column field="Monto" label="Monto" numeric v-slot="props">
              <span class="has-text-weight-semibold">${{ props.row.Monto.toFixed(2) }} USD</span>
            </b-table-column>
  
            <b-table-column field="TotalPagado" label="Total Pagado" numeric v-slot="props">
              <span class="has-text-success has-text-weight-semibold">${{ props.row.TotalPagado.toFixed(2) }} USD</span>
            </b-table-column>
  
            <b-table-column field="FechaCreacion" label="Fecha Creación" centered v-slot="props">
              <span class="has-text-grey">{{ formatDate(props.row.FechaCreacion) }}</span>
            </b-table-column>

            <b-table-column field="StripeId" label="Stripe ID" v-slot="props">
              <b-tag v-if="props.row.StripeId" type="is-success" size="is-small">
                {{ props.row.StripeId }}
              </b-tag>
              <span v-else class="has-text-grey">—</span>
            </b-table-column>
          </b-table>
        </div>
      </div>
  
      <div v-else class="has-text-centered" style="padding: 40px;">
        <b-icon icon="credit-card" pack="fas" size="is-large" type="is-grey-light"></b-icon>
        <p class="title is-5 has-text-grey">No hay datos de financiamiento disponibles</p>
        <p class="subtitle is-6 has-text-grey">Los datos de mensualidades aparecerán aquí cuando estén disponibles.</p>
        
        <b-button 
          type="is-primary" 
          icon-left="plus"
          @click="openManualPaymentModal"
          :disabled="!member._id"
          style="margin-top: 1rem;"
        >
          Registrar Primer Pago
        </b-button>
      </div>

      <ManualPaymentModal 
        v-model="showManualPaymentModal"
        :member="member"
        @payment-success="onPaymentSuccess"
        @close="onModalClose"
      />
    </div>
  </template>
  
  <script>
  import ManualPaymentModal from './ManualPaymentModal.vue'

  export default {
    name: 'FinancingMember',
    components: {
      ManualPaymentModal
    },
    props: {
      financingData: {
        type: Object,
        default: () => ({
          member: {},
          mensualidades: [],
          total: 0
        })
      },
      loading: {
        type: Boolean,
        default: false
      },
      member: {
        type: Object,
        default: () => ({})
      }
    },
    data() {
      return {
        showManualPaymentModal: false
      }
    },
    computed: {
      processedFinancingData() {
        if (!this.financingData.mensualidades || this.financingData.mensualidades.length === 0) {
          return this.financingData;
        }
        
        const processedData = { ...this.financingData };
        processedData.mensualidades = this.calculateCumulativePayments(this.financingData.mensualidades);
        
        return processedData;
      }
    },
    methods: {
      calculateCumulativePayments(mensualidades) {
        const sortedMensualidades = [...mensualidades].sort((a, b) => a.Mensualidad - b.Mensualidad);
        
        const totalRealAPagar = sortedMensualidades.reduce((sum, mensualidad) => sum + mensualidad.Monto, 0);
        
        let totalPagadoAcumulado = 0;
        
        const updatedMensualidades = sortedMensualidades.map(mensualidad => {
          totalPagadoAcumulado += mensualidad.Monto;
          
          const porcentajePagado = (totalPagadoAcumulado / totalRealAPagar) * 100;
          
          const faltaPorPagar = totalRealAPagar - totalPagadoAcumulado;
          
          return {
            ...mensualidad,
            TotalAPagar: parseFloat(totalRealAPagar.toFixed(2)),
            TotalPagado: parseFloat(totalPagadoAcumulado.toFixed(2)),
            PorcentajePagado: parseFloat(porcentajePagado.toFixed(2)),
            FaltaPorPagar: parseFloat(Math.max(0, faltaPorPagar).toFixed(2))
          };
        });
        
        return updatedMensualidades.sort((a, b) => b.Mensualidad - a.Mensualidad);
      },
      
      formatDate(timestamp) {
        if (!timestamp) return '';
        return new Date(timestamp).toLocaleDateString('es-ES', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      },
      
      openManualPaymentModal() {
        if (!this.member._id) {
          this.$buefy.toast.open({
            message: 'Debe seleccionar un member válido',
            type: 'is-warning',
            duration: 3000
          })
          return
        }
        
        this.showManualPaymentModal = true
      },
      
      onPaymentSuccess(paymentData) {
        this.$emit('payment-added', paymentData)
        
        this.$buefy.notification.open({
          duration: 5000,
          message: `Pago registrado exitosamente para la mensualidad ${paymentData.financing.Mensualidad}`,
          position: 'is-top-right',
          type: 'is-success',
          hasIcon: true
        })
      },
      
      onModalClose() {
        this.showManualPaymentModal = false
      }
    }
  }
  </script>
  
  <style scoped>
  .box {
    box-shadow: none !important;
    -webkit-box-shadow: none !important;
  }
  
  .b-table {
    font-size: 0.9rem;
  }
  
  .tag.is-primary {
    font-weight: 600;
  }
  
  .has-text-weight-semibold {
    font-weight: 600;
  }
  </style>